/**
 * What a Statistic Weight means, kept out of the components that show it.
 *
 * A scored Binding may declare what the statistic it binds is worth in the
 * Impact Factor (ADR-0040). The number has always been a *relative* one — the
 * ranker divides by the weights of the statistics the player actually carries —
 * and this module is what lets the screens call it a **percentage** and be
 * telling the truth.
 *
 * **Why a percentage needs no migration and re-ranks nothing.** The Impact
 * Factor is a weighted *mean*: `app/domain/ranking.py::_aggregate` accumulates
 * `weight * score` over the statistics the player carries and divides by the
 * weights of those same statistics. The divisor is therefore always the sum of
 * the weights in play, so scaling every weight by the same factor cannot change
 * a single score. `20` and `10` rank exactly as `2` and `1` do. A percentage is
 * not a new kind of weight; it is the existing weight read against a total of
 * 100, which is the scale a person can actually hold in their head.
 *
 * **What a percentage promises that a bare multiplier did not**, and therefore
 * what this module exists to keep honest:
 *
 * - That the numbers have a whole. `parseWeightPercent` refuses above 100.
 * - That the number against a row is the say that row gets. `sharesOf` computes
 *   it the way the ranker does, which means counting an undeclared Binding as
 *   **1** and not as nothing — ADR-0040's "a Binding declaring no weight weighs
 *   1", which is what keeps every file published before the column existed
 *   exactly where it was. **Nothing renders it at present**: the per-row share
 *   line under each Weight box was removed as clutter, a hundred-odd rows of it
 *   on a thirteen-report source. It is kept, and kept tested, because it is
 *   this module's statement of how the ranker's divisor is built — the thing
 *   `weightNotice` is checked against and the thing any future display of a
 *   share has to agree with.
 * - That a declared set of percentages adds up. `weightNotice` says so when a
 *   fully-weighted review totals something other than 100, and `MULTIPLIER_CEILING`
 *   is why it does not fire on the curated multiplier mapping the feature was
 *   built for, which has no whole to fall short of.
 *
 * **It deliberately says nothing about a review that mixes percentages with
 * blanks**, though the blanks do each hold about a hundredth of the ranking.
 * That was warned about once and the warning was removed: the state is a normal
 * one on these screens, so the banner fired on ordinary work rather than on a
 * mistake. The per-row shares that used to show the arithmetic are gone from
 * the Weight column too, so the screens now say nothing at all about a mixed
 * review — which is the deliberate consequence of both removals rather than an
 * oversight of either.
 *
 * **The share is the share a complete player gets.** `_aggregate`'s divisor is
 * the weights of the statistics *this player* carries, so a player missing a
 * heavily-weighted statistic is averaged over what he has and the rest of his
 * statistics each count for more than the figure shown here. That is ADR-0040's
 * `mask` and it is deliberate — dividing by the profile's total instead would
 * score a specialist as though he had recorded nought for everything nobody
 * asked him for. So these shares describe the declaration, not any one row of
 * the published file, and the screens say so rather than implying otherwise.
 *
 * No imports, so `npm test` can execute it directly: the frontend ships no test
 * runner and bare `node --experimental-strip-types` cannot load React or Vite.
 */

/**
 * What the ranker gives a Binding that declares no weight.
 *
 * `_aggregate` reads a missing weight as `1.0`. Mirrored here as a named
 * constant rather than a literal because every interesting thing in this module
 * is a comparison against it, and because a screen that disagreed with the
 * ranker about this number would be worse than one that showed nothing.
 */
export const UNDECLARED_WEIGHT = 1;

/**
 * Above which a declared weight is being read as a percentage rather than as a
 * multiplier, for the purpose of warning about blanks beside it.
 *
 * The two framings share one column and both are legitimate. **Measured against
 * the shipped `REVIEW` in `app/domain/templates_mls_next_pro.py` on 2026-10-06**:
 * 70 scored rows, 27 of them weighted from 0.2 to 1.6 summing to 17.25 — a mean
 * of 0.64 — and 43 left blank. Those blanks weigh 1, which is above the declared
 * mean rather than a fraction of it, and that is the intended shipped state of a
 * real profile. Somebody thinking in percentages types 40 and 35 and leaves four
 * rows blank, and *those* blanks hold about 1% each.
 *
 * (ADR-0040's body says 71 scored where the template ships 70. The figure here is
 * the one measured off the code; the discrepancy is noted rather than averaged.)
 *
 * Mixed-and-blank is only worth a warning in the second case, so the test is
 * whether the mean declared weight has left the multiplier's neighbourhood —
 * twice the undeclared weight, which puts the blanks at under half the say of
 * an average declared row before anything is said about them.
 */
const MULTIPLIER_CEILING = UNDECLARED_WEIGHT * 2;

/** How near a declared total has to be to 100 to pass without comment. */
const TOTAL_TOLERANCE = 0.01;

/**
 * The largest weight the API will store (`BindingIn.weight`, `le=1000`).
 *
 * Mirrored because the cell has to agree with it in both directions. Refusing
 * *below* this bound would lock a row the API considers perfectly legal: ADR-0040's
 * amendment keeps `le=1000` precisely so a weight a client already holds, or a
 * Source Template composed, does not become unsaveable — and a cell that refused
 * at 100 would render exactly that row as a permanent error with no share and no
 * way to edit it in place, which is the opposite of what the loose bound is for.
 * Accepting *above* it would offer a value the save is going to reject.
 */
const API_CEILING = 1000;

/** A scored row as this module needs it: something to key by, and a weight. */
export interface WeightedStat {
  key: string;
  /** The declared percentage, or null for nothing declared — which weighs 1. */
  weight: number | null;
}

/** One row's say in the ranking, as the ranker would give it. */
export interface StatShare {
  key: string;
  /** Whether somebody typed this, as against it falling back to 1. */
  declared: boolean;
  /** The weight in play: what was declared, or `UNDECLARED_WEIGHT`. */
  weight: number;
  /** That weight as a percentage of every weight in play. */
  share: number;
}

/**
 * A refusal with a message, or a weight — `null` being nothing declared.
 *
 * An accepted weight may still carry a `warning`. The two are different things
 * and the distinction is the whole of how this module treats the gap between
 * "not a percentage" and "not storable": a weight above 100 is not a share of
 * anything and is worth saying so, but it is legal, already in the database in
 * principle, and must stay editable. Only a weight the API would reject is a
 * refusal.
 */
export type ParsedWeight =
  | { ok: true; weight: number | null; warning?: string }
  | { ok: false; message: string };

/**
 * What was typed into a Weight box.
 *
 * Blank is handled before anything else and on purpose: `Number("")` and
 * `Number(" ")` are both `0`, so parsing first and range-checking after would
 * turn a stray space into a statistic weighted nought — which is the one value
 * ADR-0040 refuses outright.
 */
export function parseWeightPercent(text: string): ParsedWeight {
  const trimmed = text.trim();
  if (trimmed === "") return { ok: true, weight: null };

  // `Number` rather than `parseFloat`, which reads "1.2.3" as 1.2 and "20%" as
  // 20 — taking the leading digits of something nobody meant as a number and
  // ranking on them.
  const value = Number(trimmed);
  if (!Number.isFinite(value)) {
    return {
      ok: false,
      message: "That is not a number. A weight is a percentage, like 20 or 7.5.",
    };
  }
  if (value <= 0) {
    return {
      ok: false,
      message:
        "A weight has to be above nought. A statistic that should not count towards the " +
        "ranking is Carried or Ignored, which the Role already says.",
    };
  }
  if (value > API_CEILING) {
    return {
      ok: false,
      message: `Above ${API_CEILING}, which is more than a weight can be stored as.`,
    };
  }
  if (value > 100) {
    // Accepted, not refused. See `API_CEILING`: this is the band the API keeps
    // legal on purpose, so the cell's job is to let somebody see it and fix it
    // rather than to lock the row.
    return {
      ok: true,
      weight: value,
      warning: "A weight is a percentage, so 100 is the whole ranking.",
    };
  }
  return { ok: true, weight: value };
}

/**
 * Whether what is typed so far could still become a valid weight.
 *
 * `parseWeightPercent` is right to refuse nought, and "0" and "0." are both
 * nought — but they are also how "0.5" is typed. A cell that surfaced every
 * refusal on every keystroke would therefore flash a red error twice on the way
 * to a value it was going to accept, which trains a reader to ignore it.
 *
 * The pending set is exactly "nothing but zeroes and a dot", which covers every
 * prefix of a valid weight that is not itself valid: `0`, `0.`, `00`, `0.0` and
 * the bare `.` that begins `.5`. Anything else that fails to parse — `abc`,
 * `101`, `20%` — is not a prefix of anything acceptable, so holding it back
 * would only delay the message.
 *
 * Blank is **not** pending: nothing declared is a finished answer, and the one
 * most rows give.
 *
 * A caller shows the refusal for a pending value when the box is left rather
 * than as it is typed, so somebody who really did type `0` and walk away still
 * finds out.
 */
export function isPendingWeight(text: string): boolean {
  const trimmed = text.trim();
  if (trimmed === "") return false;
  return /^0*\.?0*$/.test(trimmed);
}

/**
 * Each row's share of the ranking, in declaration order.
 *
 * Over every row given, declared or not, because the divisor the ranker uses
 * includes the undeclared ones at 1 apiece. Callers pass the **scored** rows
 * only: a `carried` or `ignored` field has no say in the Impact Factor and
 * including it would shrink everyone else's share by a row that does not rank.
 */
export function sharesOf(stats: WeightedStat[]): StatShare[] {
  const inPlay = stats.map((stat) => ({
    key: stat.key,
    declared: stat.weight != null,
    weight: stat.weight ?? UNDECLARED_WEIGHT,
  }));
  const total = inPlay.reduce((sum, stat) => sum + stat.weight, 0);
  // Only reachable from an empty list, since a declared weight is refused at or
  // below nought and an undeclared one is 1. Guarded anyway: a NaN share would
  // render as a cell reading "NaN%" with nothing to say why.
  if (total <= 0) return inPlay.map((stat) => ({ ...stat, share: 0 }));
  return inPlay.map((stat) => ({ ...stat, share: (stat.weight / total) * 100 }));
}

/**
 * What the declared percentages add up to.
 *
 * Declared only. Counting the blanks at 1 apiece would make a review where two
 * rows add to 80 and six are untouched read as a finished 86 — the state this
 * figure exists to expose.
 */
export function declaredTotal(stats: WeightedStat[]): number {
  return stats.reduce((sum, stat) => sum + (stat.weight ?? 0), 0);
}

/** Something worth saying about a set of weights, or nothing. */
export interface WeightNotice {
  tone: "warning" | "info";
  title: string;
  message: string;
}

/**
 * The one thing worth saying about a review's weights, or nothing.
 *
 * Ordered by what would actually bite, and silent in the two states that are
 * not problems — nothing declared at all, which is how every profile ranked
 * before this column existed, and percentages that add to 100.
 */
export function weightNotice(stats: WeightedStat[]): WeightNotice | null {
  if (stats.length === 0) return null;

  const declared = stats.filter((stat) => stat.weight != null);
  if (declared.length === 0) return null;

  const blanks = stats.length - declared.length;
  const total = declaredTotal(stats);
  const mean = total / declared.length;

  // **Both notices are about percentages, so both are gated on the weights
  // being percentages.** Forced by the test below standing in for MLS NEXT Pro's
  // mapping — a hand-written five-row sample, not the mapping itself, which no
  // test here imports. Without this line that sample tripped the second notice,
  // which read "the weights add to 2.3%, not 100%": noise on the one profile the
  // feature was built for, and on the real mapping it would read 17.25%. A
  // relative multiplier has no whole to fall short of, and its blanks weigh 1 —
  // above the declared mean rather than a fraction of it — so neither thing this
  // function warns about is true of it.
  if (mean <= MULTIPLIER_CEILING) return null;

  // **A mixed review says nothing, by request.** This function used to warn
  // here that the blanks each hold about a hundredth of the ranking. The
  // warning was accurate and it was removed anyway: the state it describes is
  // a normal and deliberate one on these screens — one dominant statistic and
  // a tail of unweighted ones is a review somebody meant — so the banner fired
  // on ordinary work rather than on a mistake, which is how a reader learns to
  // skip the banner that matters.
  //
  // The per-row shares are still shown in the Weight column, so the arithmetic
  // is still on the screen for anybody who wants it; what has gone is the
  // unprompted verdict about it.
  //
  // It returns rather than falling through, because the total below is only
  // meaningful once every scored row is counted. A review with blanks in it is
  // not *trying* to add to 100, so reporting that it does not would replace one
  // unwanted banner with another.
  if (blanks > 0) return null;

  if (Math.abs(total - 100) > TOTAL_TOLERANCE) {
    return {
      tone: "info",
      title: `The weights add to ${formatPercent(total)}%, not 100%`,
      message:
        "Which still ranks: the Impact Factor divides by the weights of the statistics a " +
        `player carries, so these count relative to each other and the shares shown are ` +
        `shares of ${formatPercent(total)}%. Worth a look only if a row is missing one.`,
    };
  }

  return null;
}

/**
 * A percentage for reading, not for arithmetic.
 *
 * Named `formatPercent` rather than `format`: it was the latter, and its one
 * import site had to alias it to say what it formatted. A module-level export
 * called `format` tells a reader nothing about what it takes.
 *
 * Two decimals at most and no trailing zeroes, so 33.333… reads as 33.33 and 20
 * reads as 20 rather than as 20.00 in a column where most values are whole.
 */
export function formatPercent(value: number): string {
  return String(Math.round(value * 100) / 100);
}

/**
 * What a row's Weight becomes when the statistic in the next cell changes.
 *
 * The Stat Dictionary entry carries a `default_weight`, and until now only the
 * server read it: `discovery.py::_index_dictionary` copies it onto every
 * Binding it auto-matches, and `NewStatisticForm` hands it back on the row the
 * statistic was invented from. The one route that did not was the ordinary one
 * — picking an existing entry out of the menu — so a statistic that *has* a
 * declared weight was bound at blank, and the screen showed no sign that the
 * dictionary had an answer. Three ways in, two of which filled the box.
 *
 * The rule is the same one the two existing routes already follow, written once
 * here so it is a test rather than a paragraph repeated in two screens:
 *
 * - **A blank box takes the new entry's default.** Nothing is overwritten,
 *   because nothing was declared.
 * - **A weight somebody typed is kept.** Choosing a statistic is not a reason to
 *   discard the number a reviewer put against this field, and that is exactly
 *   the loss `onCreated` was fixed for once already.
 * - **A weight this rule put there follows the statistic.** Picking *Assists*
 *   and then correcting it to *Shots* must not leave Assists' default sitting
 *   on a Shots row, where it would read as a decision. Recognised by value
 *   against the previous entry's default rather than by a flag, which keeps the
 *   row shape — saved, templated and round-tripped by both screens — untouched.
 *
 * The last two collide when a typed weight happens to equal the old entry's
 * default, and the collision resolves in favour of following the statistic. It
 * costs a retype of a number that is still on screen; the alternative costs a
 * silently wrong weight on a correction nobody would think to re-check.
 *
 * `nextDefault` of null clears a weight this rule placed, rather than leaving
 * the last one: blank means "nothing declared" (ADR-0040) and that is precisely
 * what an entry with no `default_weight` declares.
 */
export function weightOnStatChange({
  weight,
  previousDefault,
  nextDefault,
}: {
  /** What the row's Weight cell holds now. */
  weight: number | null;
  /** `default_weight` of the entry being moved away from, if any. */
  previousDefault: number | null;
  /** `default_weight` of the entry being chosen, if any. */
  nextDefault: number | null;
}): number | null {
  if (weight === null) return nextDefault;
  if (previousDefault !== null && weight === previousDefault) return nextDefault;
  return weight;
}
